import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { Observable, Subject, combineLatest, filter, takeUntil } from 'rxjs';

import {
  Feature,
  FeatureCommands,
  FeatureNames,
  FeaturePropertyKey,
  HeatingConfigurationSecondaryHeatGeneratorModeOnErrorCommand,
  HeatingConfigurationSecondaryHeatGeneratorModeOnErrorProperties,
  HeatingConfigurationSecondaryHeatGeneratorModeOnErrorValue,
  HeatingRodHeatingCommands,
  HeatingRodHeatingProperties,
  HeatingRodStatisticsProperties,
  HeatingRodStatusProperties,
  HeatingRodsHeatGenerationEntityProperties,
  IsNumberValueProperty,
} from '@vi-iot/client';

import { AuthStore, Helpers, InstallationFeaturesStore } from '@store';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatTooltip } from '@angular/material/tooltip';
import { matTooltipSetter } from 'src/app/shared/functions/mat-tooltip-setter/mat-tooltip-setter';
import { ElectricHeaterService } from '../electric-heater.service';

@Component({
  selector: 'app-electric-heater-overlay',
  templateUrl: './electric-heater-overlay.component.html',
  styleUrls: ['./electric-heater-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ElectricHeaterOverlayComponent implements OnInit, OnDestroy {
  @ViewChildren(MatTooltip)
  set matTooltip(queryList: QueryList<MatTooltip>) {
    matTooltipSetter(queryList);
  }

  readonly featurePropertyKey = FeaturePropertyKey;
  readonly featureCommands = FeatureCommands;
  readonly heatingConfigurationSecondaryHeatGeneratorModeOnErrorValue = HeatingConfigurationSecondaryHeatGeneratorModeOnErrorValue;
  getHeatingRodStatusValue = Helpers.getHeatingRodStatusValue;
  heatingRodHeating$: Observable<
    Feature<HeatingRodsHeatGenerationEntityProperties, HeatingRodHeatingCommands>
  >;
  heatingRodDhw$: Observable<Feature<HeatingRodHeatingProperties, HeatingRodHeatingCommands>>;
  heatingRodStatus$: Observable<Feature<HeatingRodStatusProperties, never>>;
  heatingRodStatistics$: Observable<Feature<HeatingRodStatisticsProperties, never>>;
  heatingRodMaximumOutsideTemperature$: Observable<Feature<IsNumberValueProperty, never>>;
  heatingConfigurationDhwHeater$: Observable<
    Feature<HeatingRodHeatingProperties, HeatingRodHeatingCommands>
  >;
  heatingConfigurationSecondaryHeatGeneratorModeOnError$: Observable<
    Feature<
      HeatingConfigurationSecondaryHeatGeneratorModeOnErrorProperties,
      HeatingConfigurationSecondaryHeatGeneratorModeOnErrorCommand
    >
  >;

  private heatingRodIndex: string;
  private deviceId: string;
  private ngUnsubscribe$: Subject<void> = new Subject();

  constructor(
    public authStore: AuthStore,
    private electricHeaterService: ElectricHeaterService,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  ngOnInit(): void {
    const heatingRodIndexOverlay$ = this.electricHeaterService.heatingRodIndexOverlay$;
    const deviceId$ = this.electricHeaterService.deviceId$;
    combineLatest([heatingRodIndexOverlay$, deviceId$])
      .pipe(
        filter(([overlay, id]) => !!overlay && !!id),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe(([heatingRodIndex, deviceId]) => {
        this.heatingRodIndex = heatingRodIndex;
        this.deviceId = deviceId;

        this.initHeatingRodIndexFeatures();
      });

    this.heatingRodDhw$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingRodDhw
    );

    this.heatingRodMaximumOutsideTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingRodMaximumOutsideTemperature
    );

    this.heatingConfigurationDhwHeater$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingConfigurationDhwHeater
    );

    this.heatingConfigurationSecondaryHeatGeneratorModeOnError$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingConfigurationSecondaryHeatGeneratorModeOnError
    );
  }

  private initHeatingRodIndexFeatures(): void {
    this.heatingRodHeating$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingRodsHeatGenerationEntity(this.heatingRodIndex),
      this.deviceId
    );

    this.heatingRodStatus$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingRodsHeatGenerationStatus(this.heatingRodIndex),
      this.deviceId
    );

    this.heatingRodStatistics$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingRodStatistics(this.heatingRodIndex),
      this.deviceId
    );
  }

  onHeatingRodHeatingChange(value: boolean): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.heatingRodHeating,
      value,
      this.featurePropertyKey.useApproved,
      '',
      value ? FeatureCommands.grant : FeatureCommands.revoke
    );
  }

  onHeatingRodDhwChange(value: boolean): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.heatingRodDhw,
      value,
      this.featurePropertyKey.useApproved,
      '',
      value ? FeatureCommands.grant : FeatureCommands.revoke
    );
  }

  onHeatingConfigurationDhwHeaterChange(value: boolean): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.heatingConfigurationDhwHeater,
      value,
      this.featurePropertyKey.useApproved,
      '',
      value ? FeatureCommands.grant : FeatureCommands.revoke
    );
  }

  onHeatingConfigurationSecondaryHeatGeneratorModeOnErrorChange(value: MatSlideToggleChange): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.heatingConfigurationSecondaryHeatGeneratorModeOnError,
      value.checked
        ? HeatingConfigurationSecondaryHeatGeneratorModeOnErrorValue.normal
        : HeatingConfigurationSecondaryHeatGeneratorModeOnErrorValue.reduced,
      this.featurePropertyKey.mode,
      this.featurePropertyKey.mode,
      FeatureCommands.setMode
    );
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
